"use client";

import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type ContentRole = "attorney-response" | "narrative-bridge" | "supporting-text";
type StoryParagraph = {
  id: string;
  text: string;
  role: ContentRole;
};
type StorySection = {
  id: string;
  phase: "self" | "us" | "now";
  eyebrow: string;
  heading: string;
  paragraphs: StoryParagraph[];
  quote?: string;
  image?: {
    src: string;
    alt: string;
    desktopPosition: "left" | "right";
  };
};
const STORY_CONTENT = {
  hero: {
    eyebrow: "Our Story",
    heading: "A Practice Shaped Over More Than Four Decades",
    introduction: "Morrie I. Levine’s legal career has developed not through marketing, but through decades of advising clients, resolving disputes, and applying careful professional judgment. This is the story of what shaped the practice, what has remained constant, and how that experience informs the work today."
  },
  chapters: [{
    id: "the-beginning",
    phase: "self",
    eyebrow: "The Beginning",
    heading: "Why Law—and What Shaped the Practice",
    paragraphs: [{
      id: "opening-context",
      role: "narrative-bridge",
      text: "Every long professional life begins with a decision about what kind of responsibility to accept. For Morrie I. Levine, entering the law was the beginning of a career centered on careful preparation, clear analysis, and consequential decisions."
    }, {
      id: "bridge-to-experience",
      role: "narrative-bridge",
      text: "Entering the profession, however, is only the starting point. The character of a legal practice is shaped when legal principles meet difficult facts, competing interests, and real human consequences."
    }]
  }, {
    id: "south-florida",
    phase: "us",
    eyebrow: "Hollywood and South Florida",
    heading: "Rooted in the Community",
    paragraphs: [{
      id: "community-bridge",
      role: "narrative-bridge",
      text: "A lawyer’s judgment does not develop in isolation. Over more than forty years, Morrie I. Levine’s approach has been tested and refined through sustained work with the businesses, families, attorneys, and institutions of South Florida."
    }, {
      id: "community-sign",
      role: "supporting-text",
      text: "The roadway sponsorship sign pictured here reflects more than public visibility. It represents a longstanding, active connection with Hollywood and the surrounding South Florida community."
    }, {
      id: "practice-context",
      role: "supporting-text",
      text: "This practice is informed by the real-world outcomes of commercial agreements, property transactions, litigation, and attorney's fee disputes. The documents we draft and the advice we offer are grounded in decades of observing how courts interpret agreements and how disputes are won or avoided."
    }],
    image: {
      src: "/img/artifact_img_3iw8112c",
      alt: "Morrie I. Levine standing beside his sponsored traffic sign in Hollywood, Florida",
      desktopPosition: "right"
    }
  }, {
    id: "professional-principle",
    phase: "us",
    eyebrow: "A Constant Principle",
    heading: "Professional Judgment Before Action",
    paragraphs: [{
      id: "principle-transition",
      role: "narrative-bridge",
      text: "Decades of practice do more than accumulate experience. They clarify which principles remain dependable when information is incomplete, pressure is high, and the correct path is not immediately obvious."
    }, {
      id: "preparation",
      role: "supporting-text",
      text: "The work always begins with meticulous preparation: identifying the relevant facts, reviewing the governing documents, separating assumptions from evidence, and understanding the practical consequences of each available course of action."
    }],
    quote: "My goal is for clients to leave a consultation with a clear, realistic understanding of where they stand, what their options are, and exactly what should happen next."
  }, {
    id: "before-the-first-conversation",
    phase: "now",
    eyebrow: "Before the First Conversation",
    heading: "What the First Conversation Is For",
    paragraphs: [{
      id: "bridge-to-now",
      role: "narrative-bridge",
      text: "The value of a long professional history is not merely retrospective. It matters because clients often seek legal guidance when the facts are unsettled, the risks are difficult to measure, and the next decision carries lasting consequences."
    }, {
      id: "consultation-standard",
      role: "narrative-bridge",
      text: "An initial consultation is not a sales presentation. It is the beginning of a disciplined diagnostic process: clarifying the matter, identifying what is known, determining what is missing, and deciding what requires attention next."
    }]
  }] satisfies StorySection[],
  closing: {
    eyebrow: "The Next Step",
    heading: "Begin by Clarifying the Matter",
    body: "Sound legal decisions begin with a clear understanding of the facts, the documents, the risks, and the available options.",
    supportingBody: "This office does not promise predetermined answers or guaranteed outcomes. Our first responsibility is to evaluate your matter carefully and explain the appropriate next step.",
    linkLabel: "Contact the Office",
    linkTo: "/contact"
  }
};
function StoryImage({
  src,
  alt
}: {
  src: string;
  alt: string;
}) {
  return <figure className="w-full">
    <div className="aspect-[16/10] w-full overflow-hidden rounded-lg shadow-sm">
<img src="https://probable-basilisk.10web.cloud/wp-content/uploads/2026/07/MIL-with-his-sponsored-traffic-sign.jpg" alt={alt} loading="lazy" decoding="async" className="h-full w-full scale-[0.97] object-cover object-[center_45%] transition-transform duration-500 hover:scale-[0.99]" />    </div>
  </figure>;
}
function NarrativeParagraph({
  paragraph,
  sectionId
}: {
  paragraph: StoryParagraph;
  sectionId: string;
}) {
  return <p data-editable-field={`${sectionId}.${paragraph.id}`} data-content-role={paragraph.role} className={paragraph.role === "narrative-bridge" ? "text-base leading-relaxed text-foreground md:text-lg" : "text-base leading-relaxed text-muted-foreground md:text-lg font-light"}>
      {paragraph.text}
    </p>;
}
function StoryChapter({
  section,
  index
}: {
  section: StorySection;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.12
  });
  const headingId = `${section.id}-heading`;
  const hasImage = Boolean(section.image);
  const imageOnLeft = section.image?.desktopPosition === "left";
  return <section id={section.id} aria-labelledby={headingId} data-narrative-phase={section.phase} className={index % 2 === 0 ? "border-t border-border bg-background px-6 py-12 text-foreground md:px-10 lg:px-16 lg:py-20" : "border-t border-border bg-muted/20 px-6 py-12 text-foreground md:px-10 lg:px-16 lg:py-20"}>
      <div ref={ref} className={`mx-auto max-w-7xl transition-all duration-700 ease-out motion-reduce:transform-none motion-reduce:transition-none ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
        <div className={hasImage ? "grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16" : "max-w-3xl"}>
          {}
          <div className={hasImage && imageOnLeft ? "max-w-3xl lg:col-start-2 lg:row-start-1" : "max-w-3xl lg:col-start-1 lg:row-start-1"}>
            <p data-editable-field={`${section.id}.eyebrow`} className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-secondary/90">
              {section.eyebrow}
            </p>

            <h2 id={headingId} data-editable-field={`${section.id}.heading`} className="font-serif text-3xl font-normal leading-[1.15] tracking-tight text-primary md:text-4xl lg:text-5xl">
              {section.heading}
            </h2>

            <div className="mt-6 space-y-5">
              {section.paragraphs.map(paragraph => <NarrativeParagraph key={paragraph.id} paragraph={paragraph} sectionId={section.id} />)}
            </div>

            {section.quote?.trim() ? <blockquote data-editable-field={`${section.id}.quote`} data-content-role="attorney-response" className="mt-8 border-l-2 border-secondary pl-6 italic">
                <p className="font-serif text-lg leading-relaxed text-primary/95 md:text-xl">
                  “{section.quote}”
                </p>
                <cite className="mt-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground not-italic">
                  — Morrie I. Levine
                </cite>
              </blockquote> : null}
          </div>

          {}
          {section.image ? <div className={imageOnLeft ? "lg:col-start-1 lg:row-start-1" : "lg:col-start-2 lg:row-start-1"}>
              <StoryImage src={section.image.src} alt={section.image.alt} />
            </div> : null}
        </div>
      </div>
    </section>;
}
export default function OurStoryPage() {
  const {
    hero,
    chapters,
    closing
  } = STORY_CONTENT;
  return <main id="our-story-page" className="bg-background text-foreground">
      {}
      

      {}
      {chapters.map((chapter, index) => <StoryChapter key={chapter.id} section={chapter} index={index} />)}

      {}
      <section id="next-step" aria-labelledby="next-step-heading" data-narrative-phase="now" className="border-t border-b border-border bg-muted/10 px-6 py-16 text-foreground md:px-10 lg:px-16 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p data-editable-field="closing.eyebrow" className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-secondary/90">
              {closing.eyebrow}
            </p>

            <h2 id="next-step-heading" data-editable-field="closing.heading" className="font-serif text-3xl font-normal leading-[1.15] tracking-tight text-primary md:text-4xl lg:text-5xl">
              {closing.heading}
            </h2>

            <div className="mt-6 space-y-5">
              <p data-editable-field="closing.body" data-content-role="narrative-bridge" className="text-base leading-relaxed text-foreground md:text-lg">
                {closing.body}
              </p>

              <p data-editable-field="closing.supportingBody" data-content-role="supporting-text" className="text-base leading-relaxed text-muted-foreground md:text-lg font-light">
                {closing.supportingBody}
              </p>
            </div>

            <div className="mt-8">
              <Link to={closing.linkTo} className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none">
                {closing.linkLabel}
                <span aria-hidden="true" className="ml-2">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>;
}